import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Radio, ArrowRight, Sparkles, Palette } from 'lucide-react';
import { Card } from '../components/ui/card';

const HomeSelector = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-purple-900 text-white flex items-center justify-center p-4">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/50">
              <Radio className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-6xl md:text-8xl font-black mb-4 leading-none">
            WVOD <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">POWER 93</span>
          </h1>
          <p className="text-2xl text-gray-400 mb-2">The Voice of the Diaspora</p>
          <p className="text-lg text-gray-500">Choose Your Experience</p>
        </div>

        {/* Design Selector Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Original Design Card - Links to actual wvod93.com */}
          <Card 
            onClick={() => window.location.href = 'https://www.wvod93.com/'}
            className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl p-8 hover:border-orange-500/50 hover:shadow-2xl hover:shadow-orange-500/20 transition-all duration-300 cursor-pointer group transform hover:scale-105"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Palette className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold group-hover:text-orange-400 transition-colors">Original Site</h2>
                <p className="text-sm text-gray-400">Current WVOD93.com</p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 text-gray-300">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span>Visit www.wvod93.com</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span>Radio.co live player & schedule</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span>Broadcast schedule & shows</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span>News, culture & weather</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span>All current features</span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <span className="text-sm text-gray-400">Current Website</span>
              <ArrowRight className="w-5 h-5 text-orange-400 group-hover:translate-x-2 transition-transform" />
            </div>
          </Card>

          {/* Design 3 - Modernized Card */}
          <Card 
            onClick={() => navigate('/design3')}
            className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl p-8 hover:border-purple-500/50 hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-300 cursor-pointer group transform hover:scale-105"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold group-hover:text-purple-400 transition-colors">Modern Design</h2>
                <p className="text-sm text-gray-400">New Glassmorphic Experience</p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 text-gray-300">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <span>Glassmorphic modern UI design</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <span>Purple/pink gradient accents</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <span>Smooth animations & transitions</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <span>All features from original site</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <span>Enhanced visual experience</span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <span className="text-sm text-gray-400">Modern & Sleek</span>
              <ArrowRight className="w-5 h-5 text-purple-400 group-hover:translate-x-2 transition-transform" />
            </div>
          </Card>
        </div>

        {/* Quick Access Info */}
        <div className="text-center mt-12">
          <p className="text-sm text-gray-500">
            Click on any design to experience WVOD Power 93 • The Voice of the Diaspora
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.8; }
        }
        .animate-pulse {
          animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
    </div>
  );
};

export default HomeSelector;
